import { isTimeOptionInPast, todayInTz, tomorrowInTz } from './business-timezone'

/**
 * The preferred wall-clock anchor for one-shot schedules that don't show a time
 * picker (calendar quick-create, drag-reschedule). Matches the historical
 * default. Kept here so the value lives next to the slot list it's drawn from.
 */
export const PREFERRED_DEFAULT_TIME = '10:00'

/**
 * The wall-clock time-of-day slots offered everywhere we let a user schedule a
 * post (PostModal, calendar ScheduleDialog). Single source of truth so both
 * surfaces stay in lockstep — they previously each hard-coded this list. Full
 * 24h grid (00:00–23:00); past-hour filtering trims it down to what's still
 * schedulable for the chosen day.
 */
export const SCHEDULE_TIME_OPTIONS = Array.from(
  { length: 24 },
  (_, hour) => `${hour.toString().padStart(2, '0')}:00`
)

/**
 * The subset of `SCHEDULE_TIME_OPTIONS` still in the future for `date` (a
 * YYYY-MM-DD wall date) interpreted in `timezone`. For a future date this is
 * the full list; for today it drops every hour at or before "now", so the
 * scheduler can't offer a slot that has already passed. Returns `[]` when the
 * whole day is behind us (e.g. today after the last slot) — callers should
 * roll the user to the next day in that case.
 */
export function getAvailableTimeOptions(
  date: string,
  timezone: string,
  nowMs: number = Date.now()
): string[] {
  return SCHEDULE_TIME_OPTIONS.filter(t => !isTimeOptionInPast(date, t, timezone, nowMs))
}

/**
 * The band where a DEFAULT suggestion reads as intentional. Outside it the
 * "first available slot" degenerates into "Schedule for 1:00 AM" (a next-hour
 * artifact, not a recommendation) — observed live at 00:07 (F-21). Explicit
 * user picks are never clamped to this band, only the opening suggestion.
 */
const SENSIBLE_FIRST_SLOT = '08:00'
const SENSIBLE_LAST_SLOT = '21:00'

/**
 * The default {date, time} a scheduling surface should open on: the first slot
 * still available today within sensible engagement hours. Small hours anchor to
 * the preferred daytime slot instead of the literal next hour; when nothing
 * sensible is left today (late evening or day exhausted), roll to tomorrow at
 * the preferred anchor. Never lands on a past time.
 */
export function getDefaultSchedule(
  timezone: string,
  nowMs: number = Date.now()
): { date: string; time: string } {
  const today = todayInTz(timezone, nowMs)
  const next = getAvailableTimeOptions(today, timezone, nowMs)[0]
  if (next && next <= SENSIBLE_LAST_SLOT) {
    return { date: today, time: next < SENSIBLE_FIRST_SLOT ? PREFERRED_DEFAULT_TIME : next }
  }
  // `tomorrowInTz` uses calendar arithmetic so DST transitions never skip or
  // repeat a day.
  return { date: tomorrowInTz(timezone, nowMs), time: PREFERRED_DEFAULT_TIME }
}

/**
 * The wall-clock time to file a post at on an explicit `date` that has no time
 * picker (quick-create, drag-reschedule). Prefers `PREFERRED_DEFAULT_TIME` when
 * it's still in the future; once that hour has passed today, falls back to the
 * first slot still available so we don't file a post in the past. If the whole
 * day is gone it returns the preferred default unchanged — the caller committed
 * to this date, so we don't silently move it to another day.
 */
export function getScheduleTimeForDate(
  date: string,
  timezone: string,
  nowMs: number = Date.now()
): string {
  if (!isTimeOptionInPast(date, PREFERRED_DEFAULT_TIME, timezone, nowMs)) {
    return PREFERRED_DEFAULT_TIME
  }
  return getAvailableTimeOptions(date, timezone, nowMs)[0] ?? PREFERRED_DEFAULT_TIME
}
